// App.js
import ItineraryModal from './components/itinerary/ItineraryModal';
import Header from './components/header/Header';
import GoogleMapDisplay from './components/map/GoogleMapDisplay';
import ActionButtonGroup from './components/common/ActionButtonGroup';
import LocationModal from './components/itinerary/LocationModal';
import RouteLegModal from './components/itinerary/RouteLegModal';
import { calculateRoute } from "./features/routing/calculateRoute";
import { clearRoute } from './features/routing/clearRoute';
import calculateDistance from "./utils/calculateDistance";
import calculateTime from "./utils/calculateTime";
import calculateLegDetails from "./utils/calculateLegDetails";
import Discover from "./lib/Discover";
import { DEFAULT_QUANTITIES } from "./features/planner/categories";
import { ThemeContext } from "./context/ThemeContext";
import { uiThemes } from "./theme/uiThemes";
import ThemeMenu from './components/header/ThemeMenu';
import { FaInfoCircle, FaListUl } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { Tooltip, IconButton } from "@chakra-ui/react";
import { googleMapsApiKey } from "./lib/googleMapsKey";

import {
  Box,
  Flex,
  Text,
  SkeletonText,
} from "@chakra-ui/react";

import {
  useJsApiLoader,
} from "@react-google-maps/api";

import { useState, useRef, React, useEffect } from "react";

const center = { lat: 51.5033, lng: -0.1196 };

// define libraries outside of functional component to prevent useEffect() from triggering each rerender
const libraries = ["places"];

function App() {
  // loads google maps script
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries,
  }); 
  const [map, setMap] = useState(/** @type google.maps.Map */ (null));
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [distance, setDistance] = useState("");
  const [time, setTime] = useState("");
  const [categoryQuantities, setCategoryQuantities] = useState(DEFAULT_QUANTITIES);
  const [combinedStops, setCombinedStops] = useState([]);
  const [searchCoverage, setSearchCoverage] = useState({ points: [], path: [] });
  const [showSearchCoverage, setShowSearchCoverage] = useState(true);
  const [routeLegs, setRouteLegs] = useState([]);
  const [showRouteLegs, setShowRouteLegs] = useState(true);
  const [plannerMode, setPlannerMode] = useState("journey");
  const [localRadius, setLocalRadius] = useState(1500);
  const [travelMethod, setTravelMethod] = useState("walking");
  const [departureOffsetMinutes, setDepartureOffsetMinutes] = useState(0);
  const [journeyWarning, setJourneyWarning] = useState("walking");
  const [routeError, setRouteError] = useState("");
  const [isPlanningRoute, setIsPlanningRoute] = useState(false);
  const [isOpenItinerary, setIsOpenItinerary] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const getInitialTheme = () => localStorage.getItem("mapTheme") || "classic";
  const [mapTheme, setMapTheme] = useState(getInitialTheme);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [detailsFromItinerary, setDetailsFromItinerary] = useState(false);
  const [activePicker, setActivePicker] = useState(null);
  const [pickedStart, setPickedStart] = useState(null);
  const [pickedFinish, setPickedFinish] = useState(null);
  const [isPlannerOpen, setIsPlannerOpen] = useState(false);
  const [updatingStopId, setUpdatingStopId] = useState(null);
  const [selectedRouteLegIndex, setSelectedRouteLegIndex] = useState(null);
  const [changingLegMode, setChangingLegMode] = useState(null);
  const closeLocationModal = () => {
    setSelectedPlace(null);
    setDetailsFromItinerary(false);
  };

  const onCloseItinerary = () => setIsOpenItinerary(false)
  const onOpenItinerary = () => setIsOpenItinerary(true)
  const directionsRendererRef = useRef(null);
  const activeGoogleMode = travelMethod === "smart" ? "WALKING" : ({ walking: "WALKING", bicycling: "BICYCLING", transit: "TRANSIT", driving: "DRIVING" }[travelMethod] || "WALKING");

  const moveStop = async (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= combinedStops.length || isPlanningRoute) return;
    const previousStops = combinedStops;
    const nextStops = [...combinedStops];
    const [movedStop] = nextStops.splice(fromIndex, 1);
    nextStops.splice(toIndex, 0, movedStop);
    setCombinedStops(nextStops);
    setIsPlanningRoute(true);
    try {
      const results = await directionsService.route({
        origin: startRef.current?.value,
        destination: plannerMode === "local" ? startRef.current?.value : finishRef.current?.value,
        waypoints: nextStops.map((stop) => ({ location: stop.geometry.location, stopover: true })),
        optimizeWaypoints: false,
        // eslint-disable-next-line no-undef
        travelMode: google.maps.TravelMode[activeGoogleMode],
      });
      setDirectionsResponse(results);
      setDistance(calculateDistance(results));
      setTime(calculateTime(results));
      setRouteLegs(calculateLegDetails(results));
    } catch (error) {
      setCombinedStops(previousStops);
      setRouteError("non-viable");
    } finally {
      setIsPlanningRoute(false);
    }
  };

  const applyStops = async (nextStops, previousStops = combinedStops) => {
    setCombinedStops(nextStops);
    setIsPlanningRoute(true);
    try {
      const results = await directionsService.route({
        origin: startRef.current?.value,
        destination: plannerMode === "local" ? startRef.current?.value : finishRef.current?.value,
        waypoints: nextStops.map((stop) => ({ location: stop.geometry.location, stopover: true })),
        optimizeWaypoints: false,
        // eslint-disable-next-line no-undef
        travelMode: google.maps.TravelMode[activeGoogleMode],
      });
      setDirectionsResponse(results);
      setDistance(calculateDistance(results));
      setTime(calculateTime(results));
      setRouteLegs(calculateLegDetails(results));
      return true;
    } catch (error) {
      setCombinedStops(previousStops);
      setRouteError("non-viable");
      return false;
    } finally {
      setIsPlanningRoute(false);
    }
  };

  const removeStop = async (place) => {
    if (combinedStops.length <= 1) {
      window.alert("A tour must contain at least one stop.");
      return;
    }
    if (updatingStopId || !window.confirm(`Remove ${place.name} and recalculate the route?`)) return;
    setUpdatingStopId(place.place_id);
    const succeeded = await applyStops(combinedStops.filter((stop) => stop.place_id !== place.place_id));
    if (succeeded && selectedPlace?.place_id === place.place_id) closeLocationModal();
    setUpdatingStopId(null);
  };

  const regenerateStop = async (place) => {
    if (updatingStopId) return;
    setUpdatingStopId(place.place_id);
    try {
      const { lat, lng } = place.geometry.location;
      const radius = plannerMode === "local" ? localRadius : undefined;
      const response = await Discover(lat, lng, place.category || place.stopType, radius);
      const excluded = new Set(combinedStops.map((stop) => stop.place_id));
      const candidates = (response || [])
        .filter((item) => item.place_id && item.geometry?.location && !excluded.has(item.place_id) && item.business_status !== "CLOSED_PERMANENTLY")
        .sort((a, b) => ((b.rating || 0) * 2.2 + Math.log10((b.user_ratings_total || 0) + 1) * 1.4) - ((a.rating || 0) * 2.2 + Math.log10((a.user_ratings_total || 0) + 1) * 1.4));
      if (!candidates.length) throw new Error("No different place found nearby");
      const category = place.category || place.stopType;
      const replacement = { ...candidates[0], category, stopType: category };
      const succeeded = await applyStops(combinedStops.map((stop) => stop.place_id === place.place_id ? replacement : stop));
      if (succeeded && selectedPlace?.place_id === place.place_id) setSelectedPlace(replacement);
    } catch (error) {
      setRouteError("places-failed");
    } finally {
      setUpdatingStopId(null);
    }
  };

  const addStop = async (stopType) => {
    if (!directionsResponse || updatingStopId || isPlanningRoute) return;
    const typeCount = combinedStops.filter((stop) => (stop.category || stop.stopType) === stopType).length;
    if (combinedStops.length >= 10 || typeCount >= 5) {
      window.alert("An itinerary can contain up to 5 of each type and 10 stops in total.");
      return;
    }

    const valueOf = (value) => typeof value === "function" ? value() : value;
    const firstLeg = directionsResponse.routes?.[0]?.legs?.[0];
    const lastLeg = directionsResponse.routes?.[0]?.legs?.at(-1);
    const toPoint = (location) => ({
      lat: valueOf(location?.lat),
      lng: valueOf(location?.lng),
    });
    const points = [
      toPoint(firstLeg?.start_location),
      ...combinedStops.map((stop) => toPoint(stop.geometry.location)),
      toPoint(lastLeg?.end_location),
    ];
    if (points.some((point) => !Number.isFinite(point.lat) || !Number.isFinite(point.lng))) {
      setRouteError("places-failed");
      return;
    }

    let insertionIndex = 0;
    let largestGap = -1;
    for (let index = 0; index < points.length - 1; index += 1) {
      const latitude = points[index + 1].lat - points[index].lat;
      const longitude = points[index + 1].lng - points[index].lng;
      const gap = latitude * latitude + longitude * longitude;
      if (gap > largestGap) {
        largestGap = gap;
        insertionIndex = index;
      }
    }
    const searchPoint = {
      lat: (points[insertionIndex].lat + points[insertionIndex + 1].lat) / 2,
      lng: (points[insertionIndex].lng + points[insertionIndex + 1].lng) / 2,
    };

    setUpdatingStopId("__adding__");
    setRouteError("");
    try {
      const response = await Discover(searchPoint.lat, searchPoint.lng, stopType, plannerMode === "local" ? localRadius : undefined);
      const excluded = new Set(combinedStops.map((stop) => stop.place_id));
      const candidates = (response || [])
        .filter((item) =>
          item.place_id &&
          item.geometry?.location &&
          !excluded.has(item.place_id) &&
          item.business_status !== "CLOSED_PERMANENTLY" &&
          (item.rating || 0) >= 3.8
        )
        .sort((a, b) =>
          ((b.rating || 0) * 2.2 + Math.log10((b.user_ratings_total || 0) + 1) * 1.4) -
          ((a.rating || 0) * 2.2 + Math.log10((a.user_ratings_total || 0) + 1) * 1.4)
        );
      if (!candidates.length) throw new Error("No suitable place found");
      const nextStops = [...combinedStops];
      nextStops.splice(insertionIndex, 0, { ...candidates[0], category: stopType, stopType });
      const succeeded = await applyStops(nextStops);
      if (succeeded) {
        setCategoryQuantities((current) => ({ ...current, [stopType]: Math.min(5, Number(current[stopType] || 0) + 1) }));
      }
    } catch (error) {
      setRouteError("places-failed");
    } finally {
      setUpdatingStopId(null);
    }
  };

  const handleMapPick = (event) => {
    if (!activePicker || !event.latLng) return;

    const coordinateValue = `${event.latLng.lat().toFixed(6)}, ${event.latLng.lng().toFixed(6)}`;
    const targetRef = activePicker === "start" ? startRef : finishRef;
    if (targetRef.current) {
      targetRef.current.value = coordinateValue;
    }

    if (activePicker === "start") {
      setPickedStart({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    } else {
      setPickedFinish({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    }

    setActivePicker(null);
    setIsPlannerOpen(true);
  };

  useEffect(() => {
    localStorage.setItem("mapTheme", mapTheme);
  }, [mapTheme]);

  /** @type React.MutableRefObject<HTMLInputElement> */
  const startRef = useRef();
  /** @type React.MutableRefObject<HTMLInputElement> */
  const finishRef = useRef();

  useEffect(() => {
    if (!isPlannerOpen) return;
    if (pickedStart && startRef.current && !startRef.current.value) {
      startRef.current.value = `${pickedStart.lat.toFixed(6)}, ${pickedStart.lng.toFixed(6)}`;
    }
    if (pickedFinish && finishRef.current && !finishRef.current.value) {
      finishRef.current.value = `${pickedFinish.lat.toFixed(6)}, ${pickedFinish.lng.toFixed(6)}`;
    }
  }, [isPlannerOpen, pickedStart, pickedFinish]);

  // if script does not load, display SkeletonText
  if (!isLoaded) {
    return <SkeletonText />;
  }

  // eslint-disable-next-line no-undef
  const directionsService = new google.maps.DirectionsService();

  const changeRouteLegMode = async (mode) => {
    const index = selectedRouteLegIndex;
    const oldLeg = directionsResponse?.routes?.[0]?.legs?.[index];
    if (!oldLeg || changingLegMode) return;
    setChangingLegMode(mode);
    try {
      const request = {
        origin: oldLeg.start_location,
        destination: oldLeg.end_location,
        // eslint-disable-next-line no-undef
        travelMode: google.maps.TravelMode[{ walking: "WALKING", bicycling: "BICYCLING", transit: "TRANSIT", driving: "DRIVING" }[mode]],
      };
      if (mode === "transit") request.transitOptions = { departureTime: new Date() };
      const replacement = await directionsService.route(request);
      const legs = [...directionsResponse.routes[0].legs];
      legs[index] = replacement.routes[0].legs[0];
      const nextDirections = { ...directionsResponse, routes: [{ ...directionsResponse.routes[0], legs }] };
      const nextRouteLegs = calculateLegDetails(nextDirections).map((leg, legIndex) => ({ ...leg, mode: legIndex === index ? mode : routeLegs[legIndex]?.mode || travelMethod }));
      setDirectionsResponse(nextDirections);
      setRouteLegs(nextRouteLegs);
      setDistance(calculateDistance(nextDirections));
      setTime(calculateTime(nextDirections));
    } catch (error) {
      setRouteError("non-viable");
    } finally {
      setChangingLegMode(null);
    }
  };

  const onCenterMap = () => {
    map.panTo(center);
  };

  return (
    <ThemeContext.Provider value={uiThemes[mapTheme]}>
      <Flex
        position="relative"
        flexDirection="column"
        alignItems="center"
        h="100dvh"
        minH="100dvh"
        w="100%"
        overflow="hidden"
      >
        <Header
          onCenter={onCenterMap}
          onSeeItinerary={onOpenItinerary}
          setMapTheme={setMapTheme}
          mapTheme={mapTheme}
          isPlannerOpen={isPlannerOpen}
          setIsPlannerOpen={setIsPlannerOpen}
          startRef={startRef}
          finishRef={finishRef}
          travelMethod={travelMethod}
          setJourneyWarning={setJourneyWarning}
          categoryQuantities={categoryQuantities}
          setCategoryQuantities={setCategoryQuantities}
          setTravelMethod={setTravelMethod}
          departureOffsetMinutes={departureOffsetMinutes}
          setDepartureOffsetMinutes={setDepartureOffsetMinutes}
          calculateRoute={calculateRoute}
          directionsService={directionsService}
          setDirectionsResponse={setDirectionsResponse}
          setDistance={setDistance}
          setTime={setTime}
          setCombinedStops={setCombinedStops}
          setSearchCoverage={setSearchCoverage}
          setRouteLegs={setRouteLegs}
          plannerMode={plannerMode}
          setPlannerMode={setPlannerMode}
          localRadius={localRadius}
          setLocalRadius={setLocalRadius}
          journeyWarning={journeyWarning}
          routeError={routeError}
          setRouteError={setRouteError}
          isPlanningRoute={isPlanningRoute}
          setIsPlanningRoute={setIsPlanningRoute}
          activePicker={activePicker}
          setActivePicker={setActivePicker}
          distance={distance}
          time={time}
          clearRoute={() =>
            clearRoute(
              setCombinedStops,
              setDirectionsResponse,
              setDistance,
              setTime,
              setJourneyWarning,
              setRouteError,
              setIsPlanningRoute,
              setPickedStart,
              setPickedFinish,
              startRef,
              finishRef,
              directionsRendererRef,
              setSearchCoverage,
              setRouteLegs
            )
          }
          directionsRendererRef={directionsRendererRef}
        />
        <Box position="absolute" left={0} top={0} h="100%" w="100%">
          <GoogleMapDisplay
            center={center}
            map={map}
            setMap={setMap}
            directionsResponse={directionsResponse}
            combinedStops={combinedStops}
            startLabel={startRef.current?.value}
            finishLabel={finishRef.current?.value}
            pickedStart={pickedStart}
            pickedFinish={pickedFinish}
            activePicker={activePicker}
            onMapPick={handleMapPick}
            setSelectedLocation={setSelectedLocation}
            selectedLocation={selectedLocation}
            mapTheme={mapTheme}
            onMarkerClick={(location) => {
              setSelectedPlace(location);
              setDetailsFromItinerary(false);
            }}
            searchCoverage={searchCoverage}
            showSearchCoverage={showSearchCoverage}
            routeLegs={routeLegs}
            showRouteLegs={showRouteLegs}
            onRouteLegClick={setSelectedRouteLegIndex}
          />
        </Box>
        {activePicker && (
          <Box
            position="absolute"
            top={{ base: 4, md: 20 }}
            left="50%"
            transform="translateX(-50%)"
            zIndex="1000"
            bg={uiThemes[mapTheme].bg}
            color={uiThemes[mapTheme].text}
            border={`1px solid ${uiThemes[mapTheme].accent}`}
            borderRadius="full"
            boxShadow="lg"
            px={4}
            py={2}
          >
            <Text fontSize="sm" fontWeight="semibold">
              Click the map to set your {activePicker} point
            </Text>
          </Box>
        )}
        <LocationModal
          isOpen={!!selectedPlace}
          onClose={closeLocationModal}
          place={selectedPlace}
          stopNumber={selectedPlace ? combinedStops.findIndex((stop) => stop.place_id === selectedPlace.place_id) + 1 : null}
          onBack={detailsFromItinerary ? () => {
            setSelectedPlace(null);
            setDetailsFromItinerary(false);
            setIsOpenItinerary(true);
          } : undefined}
          updating={updatingStopId === selectedPlace?.place_id}
          onRemove={() => removeStop(selectedPlace)}
          onRegenerate={() => regenerateStop(selectedPlace)}
        />
        <RouteLegModal
          isOpen={selectedRouteLegIndex !== null}
          onClose={() => setSelectedRouteLegIndex(null)}
          leg={selectedRouteLegIndex === null ? null : routeLegs[selectedRouteLegIndex]}
          index={selectedRouteLegIndex || 0}
          changing={changingLegMode}
          onModeChange={changeRouteLegMode}
        />
        <Box
          position="absolute"
          zIndex="900"
          right={{ base: "50%", md: 4 }}
          bottom={{ base: "calc(env(safe-area-inset-bottom, 0px) + 24px)", md: "auto" }}
          top={{ base: "auto", md: "50%" }}
          transform={{ base: "translateX(50%)", md: "translateY(-50%)" }}
          bg={uiThemes[mapTheme].bg}
          border={`1px solid ${uiThemes[mapTheme].accent}`}
          borderRadius="full"
          boxShadow="lg"
          px={{ base: 1.5, md: 2 }}
          py={{ base: 2, md: 3 }}
          minW="auto"
        >
          <Flex
            gap={2}
            direction={{ base: "row", md: "column" }}
            align="center"
            justify="center"
          >
            <ActionButtonGroup
              hasRoute={!!directionsResponse}
              clearRoute={() =>
                clearRoute(
                  setCombinedStops,
                  setDirectionsResponse,
                  setDistance,
                  setTime,
                  setJourneyWarning,
                  setRouteError,
                  setIsPlanningRoute,
                  setPickedStart,
                  setPickedFinish,
                  startRef,
                  finishRef,
                  directionsRendererRef,
                  setSearchCoverage,
                  setRouteLegs
                )
              }
              onCenter={onCenterMap}
              hasSearchCoverage={searchCoverage.points.length > 0}
              showSearchCoverage={showSearchCoverage}
              onToggleSearchCoverage={() => setShowSearchCoverage((visible) => !visible)}
              showRouteLegs={showRouteLegs}
              onToggleRouteLegs={() => setShowRouteLegs((visible) => !visible)}
              infoControl={(
                <Tooltip label="About & support" hasArrow placement="left">
                  <IconButton as={RouterLink} to="/support" aria-label="About and support" icon={<FaInfoCircle />} isRound bg={uiThemes[mapTheme].bg} color={uiThemes[mapTheme].text} _hover={{ bg: `${uiThemes[mapTheme].accent}22` }} border={`1px solid ${uiThemes[mapTheme].accent}`} boxShadow="md" size="lg" />
                </Tooltip>
              )}
              themeControl={<ThemeMenu mapTheme={mapTheme} setMapTheme={setMapTheme} />}
              itineraryControl={directionsResponse ? (
                <Tooltip label="Itinerary" hasArrow placement="left">
                  <IconButton
                    aria-label="Itinerary"
                    icon={<FaListUl />}
                    onClick={onOpenItinerary}
                    isRound
                    bg={uiThemes[mapTheme].bg}
                    color={uiThemes[mapTheme].text}
                    _hover={{ bg: uiThemes[mapTheme].accent }}
                    border={`1px solid ${uiThemes[mapTheme].accent}`}
                    boxShadow="md"
                    size="lg"
                  />
                </Tooltip>
              ) : null}
            />
          </Flex>
        </Box>
        <ItineraryModal
          isOpen={isOpenItinerary}
          onClose={onCloseItinerary}
          combinedStops={combinedStops}
          distance={distance}
          time={time}
          travelMethod={travelMethod}
          routeLegs={routeLegs}
          onMoveStop={moveStop}
          updatingStopId={updatingStopId}
          onRemoveStop={removeStop}
          onRegenerateStop={regenerateStop}
          onAddStop={addStop}
          onSelectStop={(stop) => {
            setIsOpenItinerary(false);
            setSelectedPlace(stop);
            setDetailsFromItinerary(true);
          }}
          onSelectLeg={(index) => {
            setIsOpenItinerary(false);
            setSelectedRouteLegIndex(index);
          }}
        />
      </Flex>
    </ThemeContext.Provider>
  );
}

export default App;
